import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TicketPriority } from '../../../shared/models/ticket.model';
import {
  TicketArticleUiModel,
  TicketDetailUiModel,
  TicketUiModel
} from '../../../shared/ui-models/ticket.ui-model';
import {
  addTicketNote,
  assignTicket,
  categorizeTicket,
  getTicketAssignees,
  downloadTicketAttachment,
  getTicket,
  handoffTicket,
  raiseTicketPriority,
  replyToTicket,
  setTicketState
} from '../../../shared/services/data/ticket.data';
import {
  attachmentProblem,
  raisablePriorities
} from '../../../shared/services/helpers/ticket-rules.helper';
import { encodeTicketFiles, saveBlob } from '../../../shared/services/helpers/ticket-files.helper';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { LoadingData } from '../../../ui/LoadingData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { TicketFilesInput } from '../../../shared/components/group-ui/raise-ticket/ticket-files-input.component';
import {
  CHIP,
  PRIORITY_LABEL,
  PRIORITY_STYLE,
  stateStyle,
  titleCase
} from '../../shared/ticket-visuals';
import { HandoffModal } from './components/handoff-modal.component';
import { TicketContext } from './components/ticket-context.component';
import { CategorizeBanner } from './components/categorize-banner.component';
import { TicketSla } from './components/ticket-sla.component';
import { TicketThread } from './components/ticket-thread.component';

const queuePath = () => (APP_ROUTES.TICKETS_MODULE.CHILDREN as RouteConfig).QUEUE.FULL_PATH;

/**
 * Why a ticket cannot be shown. A 403 is common and expected: a ticket handed
 * to another team stays readable only by that team.
 */
const loadMessage = (error: ErrorInfo): string =>
  error.status === 403
    ? 'This ticket is not in any of your teams any more. It was probably handed off to another team.'
    : error.status === 404
    ? 'This ticket does not exist.'
    : describeLoadError(error);

const failMessage = (error: ErrorInfo, action: string): string =>
  error.status === 403
    ? `You cannot ${action} on this ticket`
    : error.status === 400
    ? `Could not ${action}: the ticket changed. Reload and try again.`
    : `Could not ${action}. Try again.`;

export const TicketDetailsContainer: React.FC = () => {
  const history = useHistory();
  const { ticketId } = useParams<{ ticketId: string }>();
  const id = Number(ticketId);

  const [ticket, setTicket] = useState<TicketDetailUiModel | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [busy, setBusy] = useState<string | undefined>();
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [showHandoff, setShowHandoff] = useState(false);
  const [showRetype, setShowRetype] = useState(false);
  /** The composer writes an internal note, or an email to the guest. */
  const [mode, setMode] = useState<'note' | 'reply'>('note');
  /** Team members this ticket can be given to; loaded when a lead opens "Assign to". */
  const [assignees, setAssignees] = useState<{ id: number; name: string }[] | null>(null);

  // Leads, managers and helpdesk admins: who this ticket can be given to.
  const canReassign = Boolean(ticket?.canReassign);
  useEffect(() => {
    if (!canReassign) return;
    getTicketAssignees(id).then((r) => setAssignees(isErrorInfo(r) ? [] : r));
  }, [canReassign, id]);

  const load = useCallback(() => {
    setLoading(true);
    return getTicket(id).then((result) => {
      setLoadError(isErrorInfo(result) ? loadMessage(result) : undefined);
      if (!isErrorInfo(result)) setTicket(result);
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  /** Applies an action's result. Actions answer with the ticket but not its thread. */
  const run = async (
    label: string,
    action: () => Promise<TicketUiModel | ErrorInfo>,
    success: (t: TicketUiModel) => string
  ) => {
    setBusy(label);
    try {
      const result = await action();
      if (isErrorInfo(result)) {
        birdToast.error(failMessage(result, label));
        return undefined;
      }
      setTicket((current) => (current ? { ...current, ...result } : current));
      birdToast.success(success(result));
      return result;
    } finally {
      setBusy(undefined);
    }
  };

  const submitNote = async () => {
    const label = mode === 'reply' ? 'send the reply' : 'add a note';
    setBusy(label);
    try {
      const attachments = files.length ? await encodeTicketFiles(files) : undefined;
      const result =
        mode === 'reply'
          ? await replyToTicket(id, note.trim(), attachments)
          : await addTicketNote(id, note.trim(), attachments);
      if (isErrorInfo(result)) {
        birdToast.error(failMessage(result, label));
        return;
      }
      if (mode === 'reply') birdToast.success(`Reply sent to ${ticket?.customer?.email}`);
      setTicket(result);
      setNote('');
      setFiles([]);
      setMode('note');
    } finally {
      setBusy(undefined);
    }
  };

  const download = async (article: TicketArticleUiModel, attachmentId: number, name: string) => {
    const result = await downloadTicketAttachment(id, article.id, attachmentId);
    if (isErrorInfo(result)) birdToast.error(`Could not download ${name}`);
    else saveBlob(result, name);
  };

  if (loading && !ticket) return <LoadingData />;
  if (loadError || !ticket) {
    return (
      <div className="flex flex-col gap-4 p-8">
        <LoadError message={loadError} onRetry={load} />
        <Link to={queuePath()} className="text-sm text-primary-700">
          ← Back to tickets
        </Link>
      </div>
    );
  }

  const raisable = raisablePriorities(ticket.priority);
  const noteProblem = attachmentProblem(files);

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section className="flex flex-col gap-3">
        <Link to={queuePath()} className="text-sm text-primary-700">
          ← Tickets
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <span className="h-8 w-1.5 rounded-full bg-secondary-500" aria-hidden="true" />
          <h1 className="font-gilroy-bold text-2xl text-neutral-500">
            <span className="text-neutral-300">#{ticket.number}</span> {ticket.title}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`${CHIP} ${PRIORITY_STYLE[ticket.priority]}`}>
            {PRIORITY_LABEL[ticket.priority]}
          </span>
          {ticket.emergency ? (
            <span className={`${CHIP} bg-secondary-500 text-white`}>Emergency</span>
          ) : null}
          <span className={`${CHIP} ${stateStyle(ticket)}`}>{titleCase(ticket.state)}</span>
          <span className={`${CHIP} bg-primary-50 text-primary-700`}>{ticket.team}</span>
          <span className={`${CHIP} bg-creams-200 text-neutral-500`}>
            {ticket.ownerName ?? 'Unassigned'}
          </span>
          {ticket.categoryLabel ? (
            <span className="text-xs text-neutral-300">{ticket.categoryLabel}</span>
          ) : null}
        </div>
      </section>

      {!ticket.categoryCode && !ticket.isClosed ? (
        <CategorizeBanner
          saving={busy === 'set the category'}
          onSubmit={async (payload) => {
            const before = ticket.team;
            const done = await run(
              'set the category',
              () => categorizeTicket(id, payload),
              (t) =>
                `Categorised as ${t.categoryCode}${t.team !== before ? `, sent to ${t.team}` : ''}`
            );
            // Routed to another team: like a handoff, re-reading it is usually a 403.
            if (done && done.team !== before) history.push(queuePath());
          }}
        />
      ) : null}

      {showRetype && ticket.categoryCode && !ticket.isClosed ? (
        <CategorizeBanner
          saving={busy === 'change the type'}
          change={{
            code: ticket.categoryCode,
            team: ticket.team,
            onCancel: () => setShowRetype(false)
          }}
          onSubmit={async (payload) => {
            const done = await run(
              'change the type',
              () => categorizeTicket(id, payload),
              (t) => `Type changed to ${t.categoryCode}`
            );
            if (done) {
              setShowRetype(false);
              load(); // the thread gains the "Type changed" note
            }
          }}
        />
      ) : null}

      {!ticket.isClosed ? (
        <section className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-md">
          <Button
            type={ButtonType.primary}
            disabled={Boolean(busy)}
            onClick={() =>
              run(
                'assign it',
                () => assignTicket(id, 'me'),
                () => 'The ticket is yours'
              )
            }
          >
            {ticket.ownerName ? 'Take over' : 'Take it'}
          </Button>
          {ticket.canReassign ? (
            // Leads, managers and helpdesk admins give the ticket to someone in its team.
            <select
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-neutral-500"
              value=""
              disabled={Boolean(busy)}
              onChange={(event) => {
                const to = Number(event.target.value);
                const name = assignees?.find((a) => a.id === to)?.name;
                if (to) {
                  run(
                    'assign it',
                    () => assignTicket(id, to),
                    () => `Assigned to ${name ?? 'them'}`
                  );
                }
              }}
            >
              <option value="">Assign to…</option>
              {(assignees ?? [])
                .filter((a) => a.id !== ticket.ownerId)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
          ) : null}
          {ticket.nextOwner ? (
            <Button
              type={ButtonType.gray}
              disabled={Boolean(busy)}
              onClick={() => setShowHandoff(true)}
            >
              Hand off to {ticket.nextOwner.teams.join(' / ')}
            </Button>
          ) : null}
          {ticket.categoryCode && !showRetype ? (
            <Button
              type={ButtonType.gray}
              disabled={Boolean(busy)}
              onClick={() => setShowRetype(true)}
            >
              Change type
            </Button>
          ) : null}
          {raisable.length ? (
            <select
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-neutral-500"
              value=""
              disabled={Boolean(busy)}
              onChange={(event) => {
                const to = event.target.value as TicketPriority;
                if (to) {
                  run(
                    'raise the priority',
                    () => raiseTicketPriority(id, to),
                    (t) => `Raised to ${PRIORITY_LABEL[t.priority]}`
                  );
                }
              }}
            >
              <option value="">Raise priority…</option>
              {raisable.map((p: TicketPriority) => (
                <option key={p} value={p}>
                  {PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          ) : null}
          <span className="ml-auto">
            <Button
              type={ButtonType.gray}
              disabled={Boolean(busy)}
              onClick={() =>
                run(
                  'close it',
                  () => setTicketState(id, 'closed'),
                  () => 'Ticket closed'
                )
              }
            >
              Close ticket
            </Button>
          </span>
        </section>
      ) : (
        <section className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-md">
          <span className="text-sm text-neutral-400">This ticket is closed.</span>
          <Button
            type={ButtonType.gray}
            disabled={Boolean(busy)}
            onClick={() =>
              run(
                'reopen it',
                () => setTicketState(id, 'open'),
                () => 'Ticket reopened'
              )
            }
          >
            Reopen
          </Button>
        </section>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
        <section className="flex flex-col gap-3">
          {ticket.articles.length ? (
            <TicketThread articles={ticket.articles} onDownload={download} />
          ) : null}

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-md">
            {ticket.customer ? (
              <div className="mb-2 flex gap-1" role="tablist">
                {(['note', 'reply'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={mode === m}
                    className={`rounded-lg px-3 py-1.5 text-sm font-gilroy-medium ${
                      mode === m
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-neutral-400 hover:bg-creams-100'
                    }`}
                    onClick={() => setMode(m)}
                  >
                    {m === 'note' ? 'Internal note' : 'Reply to guest'}
                  </button>
                ))}
              </div>
            ) : (
              <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
                Internal note
              </label>
            )}
            {mode === 'reply' && ticket.customer ? (
              <p className="mb-2 text-xs text-neutral-400">
                Emailed to {ticket.customer.name} &lt;{ticket.customer.email}&gt;. The guest sees
                this; internal notes stay internal.
              </p>
            ) : null}
            <textarea
              className="min-h-[100px] w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              maxLength={20000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={
                mode === 'reply'
                  ? 'Write to the guest'
                  : 'What you did, what you found, what is next'
              }
            />
            <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
              <TicketFilesInput files={files} onChange={setFiles} disabled={Boolean(busy)} />
              <Button
                type={ButtonType.primary}
                disabled={!note.trim() || Boolean(noteProblem) || Boolean(busy)}
                loading={busy === 'add a note' || busy === 'send the reply'}
                onClick={submitNote}
              >
                {mode === 'reply' ? 'Send reply' : 'Add note'}
              </Button>
            </div>
          </div>
        </section>

        <aside className="flex flex-col gap-5">
          <TicketSla ticket={ticket} />
          <TicketContext ticket={ticket} />
        </aside>
      </div>

      <HandoffModal
        show={showHandoff}
        next={ticket.nextOwner}
        saving={busy === 'hand it off'}
        onClose={() => setShowHandoff(false)}
        onSubmit={async (payload) => {
          const moved = await run(
            'hand it off',
            () => handoffTicket(id, payload),
            (t) => `Handed off to ${t.team}`
          );
          if (moved) {
            setShowHandoff(false);
            // The ticket now belongs to another team, and re-reading it is usually a 403.
            history.push(queuePath());
          }
        }}
      />
    </div>
  );
};
